import { useRef, useCallback, useEffect, useState } from "react";
import { Logger } from "@cede/utils";
import { THROTTLE_DELAY, MAX_RECONNECT_ATTEMPTS, MAX_RECONNECT_DELAY } from "../constants";
import type { DirectOrderBookSnapshot, UseDirectOrderBookWSProps, UseDirectOrderBookWSResult } from "../types";

const EXCHANGE_ID = "bitstamp";
const WS_URL = "wss://ws.bitstamp.net";

/**
 * Format pair symbol for Bitstamp WebSocket
 * BTC/USDT → btcusdt (lowercase, no separator)
 */
function formatSymbol(pairSymbol: string): string {
  return pairSymbol.replace("/", "").toLowerCase();
}

/**
 * Hook for connecting to Bitstamp WebSocket and subscribing to orderbook updates
 * Bitstamp uses bts:subscribe event format
 */
export const useBitstampOrderBook = ({
  pairSymbol,
  enabled = true,
}: UseDirectOrderBookWSProps): UseDirectOrderBookWSResult => {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout>(undefined);
  const throttleTimeoutRef = useRef<NodeJS.Timeout>(undefined);
  const lastUpdateTimeRef = useRef<number>(0);
  const isConnecting = useRef(false);
  const reconnectAttemptsRef = useRef<number>(0);

  const [isConnected, setIsConnected] = useState(false);
  const [orderBookData, setOrderBookData] = useState<DirectOrderBookSnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);

  const updateOrderBookData = useCallback((data: DirectOrderBookSnapshot) => {
    setOrderBookData(data);
    setError(null);
  }, []);

  const handleOrderBookUpdate = useCallback(
    (data: DirectOrderBookSnapshot) => {
      const now = Date.now();
      const timeSinceLastUpdate = now - lastUpdateTimeRef.current;

      if (timeSinceLastUpdate >= THROTTLE_DELAY) {
        lastUpdateTimeRef.current = now;
        updateOrderBookData(data);
      } else {
        if (throttleTimeoutRef.current) {
          clearTimeout(throttleTimeoutRef.current);
        }

        const remainingTime = THROTTLE_DELAY - timeSinceLastUpdate;
        throttleTimeoutRef.current = setTimeout(() => {
          lastUpdateTimeRef.current = Date.now();
          updateOrderBookData(data);
        }, remainingTime);
      }
    },
    [updateOrderBookData],
  );

  const disconnectWs = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = undefined;
    }

    if (throttleTimeoutRef.current) {
      clearTimeout(throttleTimeoutRef.current);
      throttleTimeoutRef.current = undefined;
    }

    isConnecting.current = false;
    setIsConnected(false);
    lastUpdateTimeRef.current = 0;
  }, []);

  const subscribeToOrderBook = useCallback((ws: WebSocket, symbol: string) => {
    const subscribeMessage = {
      event: "bts:subscribe",
      data: {
        channel: `order_book_${symbol}`,
      },
    };

    ws.send(JSON.stringify(subscribeMessage));
  }, []);

  const connectWs = useCallback(() => {
    if (isConnecting.current || !enabled || !pairSymbol) return;

    disconnectWs();

    isConnecting.current = true;
    setError(null);
    setOrderBookData(null);

    try {
      const ws = new WebSocket(WS_URL);

      ws.onopen = () => {
        setIsConnected(true);
        isConnecting.current = false;
        reconnectAttemptsRef.current = 0;

        const symbol = formatSymbol(pairSymbol);
        subscribeToOrderBook(ws, symbol);

        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
        }
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);

          // Handle subscription response
          if (message.event === "bts:subscription_succeeded") {
            return;
          }

          // Handle orderbook data
          // Bitstamp format: { event: "data", channel: "order_book_btcusd", data: { bids, asks, timestamp, microtimestamp } }
          if (message.event === "data" && message.data) {
            const bookData = message.data;

            if (bookData.bids && bookData.asks) {
              // Bitstamp format: [[price, size], ...]
              const bids: [number, number][] = bookData.bids.map((level: [string, string]) => [
                parseFloat(level[0]),
                parseFloat(level[1]),
              ]);

              const asks: [number, number][] = bookData.asks.map((level: [string, string]) => [
                parseFloat(level[0]),
                parseFloat(level[1]),
              ]);

              const snapshot: DirectOrderBookSnapshot = {
                timestamp: bookData.microtimestamp ? Math.floor(parseInt(bookData.microtimestamp) / 1000) : Date.now(),
                exchangeId: EXCHANGE_ID,
                pairSymbol,
                bids,
                asks,
              };

              handleOrderBookUpdate(snapshot);
            }
          }
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : "Unknown error";
          Logger.error(`[${EXCHANGE_ID}OrderBookWS] Failed to parse orderbook update:`, errorMessage, err);
          setError("Failed to parse orderbook update");
        }
      };

      ws.onerror = () => {
        isConnecting.current = false;
        setIsConnected(false);
        setError("WebSocket connection error");
      };

      ws.onclose = () => {
        isConnecting.current = false;
        setIsConnected(false);

        if (enabled && pairSymbol && reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), MAX_RECONNECT_DELAY);
          reconnectAttemptsRef.current++;

          Logger.log(
            `[${EXCHANGE_ID}OrderBookWS] Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current}/${MAX_RECONNECT_ATTEMPTS})`,
            Logger.types.INFO,
          );

          reconnectTimeoutRef.current = setTimeout(() => {
            connectWs();
          }, delay);
        } else if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
          Logger.error(`[${EXCHANGE_ID}OrderBookWS] Max reconnection attempts reached, giving up`);
          setError("Failed to reconnect after multiple attempts");
        }
      };

      wsRef.current = ws;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Unknown error";
      Logger.error(`[${EXCHANGE_ID}OrderBookWS] Failed to create WebSocket connection:`, errorMessage, err);
      isConnecting.current = false;
      setError("Failed to create WebSocket connection");
    }
  }, [pairSymbol, enabled, disconnectWs, handleOrderBookUpdate, subscribeToOrderBook]);

  useEffect(() => {
    if (!enabled || !pairSymbol) {
      disconnectWs();
      return;
    }

    connectWs();

    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        disconnectWs();
      } else if (enabled && pairSymbol) {
        connectWs();
      }
    };

    window.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      window.removeEventListener("visibilitychange", onVisibilityChange);
      disconnectWs();
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (throttleTimeoutRef.current) {
        clearTimeout(throttleTimeoutRef.current);
      }
    };
  }, [pairSymbol, enabled, connectWs, disconnectWs]);

  return {
    isConnected,
    orderBookData: orderBookData ?? undefined,
    error,
  };
};
